import FilterDate from '@/Enums/FilterDate.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import { IClientControlOption, IControlOption, IWhitelabelControlOption } from '@/Interfaces/ControlOption.interface';
import ApplyLoadingButton from '@/components/Buttons/ApplyLoadingButton';
import ClearAllButton from '@/components/Buttons/ClearAllButton';
import { mapWhitelabelToControlOption } from '@/helpers/Whitelabels';
import { areArraysOfObjectsEqual } from '@/helpers/comparators';
import { getFilterOptions } from '@/helpers/filters/filterOptions';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectWebVisitsFilterSource, selectWebVisitsFilters } from '@/store/selectors/attribution/webVisits.selectors';
import { selectMeRole } from '@/store/selectors/me.selectors';
import {
  selectRibeyeWithBeeswaxClients,
  selectRibeyeWithBeeswaxWhitelabels,
  selectSharedApiLoading,
} from '@/store/selectors/sharedApi.selectors';
import { resetFilters, setFilters } from '@/store/slices/attribution/webVisits.slice';
import { WebVisitsFilters } from '@/types/Attribution/filters.types';
import { Paper } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import FilterPopover from './FilterPopover';
import FilterSelects from './FilterSelects';

interface IProps {
  hideWhitelabels: boolean;
  hideClients: boolean;
  loadingFilterSource?: boolean;
  onApplyFilters: () => void;
}

const campaignsReset: Partial<WebVisitsFilters> = { placement: [] };
const advertisersReset: Partial<WebVisitsFilters> = { ...campaignsReset, campaigns: [], events: [] };
const clientsReset: Partial<WebVisitsFilters> = { ...advertisersReset, advertisers: [] };
const whitelabelsReset: Partial<WebVisitsFilters> = { ...clientsReset, clients: [] };

const Filters = ({ hideWhitelabels, hideClients, onApplyFilters, loadingFilterSource }: IProps) => {
  const dispatch = useAppDispatch();
  const meRole = useAppSelector(selectMeRole);
  const hasInitialized = useRef<boolean>(false);

  const whitelabels = useAppSelector(selectRibeyeWithBeeswaxWhitelabels);
  const clients = useAppSelector(selectRibeyeWithBeeswaxClients);
  const loading = useAppSelector(selectSharedApiLoading);
  const { campaigns } = useAppSelector(selectWebVisitsFilterSource);

  const {
    dateRangeStart,
    dateRangeEnd,
    dateRangeFormat,
    whitelabels: filterWhitelabels,
    clients: filterClients,
    advertisers: filterAdvertisers,
    campaigns: filterCampaigns,
    events: filterEvents,
  } = useAppSelector(selectWebVisitsFilters);

  const options = useMemo(() => {
    return getFilterOptions({
      userRole: meRole,
      whitelabels,
      selectedWhitelabels: filterWhitelabels,
      clients,
      selectedClients: filterClients,
    });
  }, [meRole, whitelabels, clients, filterClients, filterWhitelabels]);

  const _setFilters = useCallback(
    (filters: Partial<WebVisitsFilters>) => {
      dispatch(setFilters(filters));
    },
    [dispatch],
  );

  const setDateRange = useCallback(
    (range: DateRange<Moment>, format?: FilterDate) => {
      const newStartDate = range[0]?.format(DateFormat.Iso8601);
      const newEndDate = range[1]?.format(DateFormat.Iso8601);
      const isValidChangeEvent = newStartDate && newEndDate;

      if (!isValidChangeEvent) return;

      if (dateRangeStart !== newStartDate || dateRangeEnd !== newEndDate || dateRangeFormat !== format) {
        _setFilters({
          dateRangeStart: newStartDate,
          dateRangeEnd: newEndDate,
          dateRangeFormat: format,
        });
      }
    },
    [dateRangeStart, dateRangeEnd, dateRangeFormat, _setFilters],
  );

  const onWhitelabelsChange = useCallback(
    (updatedWhitelabels: IWhitelabelControlOption[] = []) => {
      if (areArraysOfObjectsEqual(updatedWhitelabels, filterWhitelabels)) {
        return;
      }
      _setFilters({
        whitelabels: updatedWhitelabels,
        ...whitelabelsReset,
      });
    },
    [_setFilters, filterWhitelabels],
  );

  const onClientsChange = useCallback(
    (updatedClients: IClientControlOption[] = []) => {
      if (areArraysOfObjectsEqual(updatedClients, filterClients)) {
        return;
      }
      _setFilters({
        clients: updatedClients,
        ...clientsReset,
      });
    },
    [_setFilters, filterClients],
  );

  const onAdvertiserChange = useCallback(
    (updatedAdvertisers: IControlOption[] = []) => {
      if (areArraysOfObjectsEqual(updatedAdvertisers, filterAdvertisers)) {
        return;
      }
      _setFilters({
        advertisers: updatedAdvertisers,
        ...advertisersReset,
      });
    },
    [_setFilters, filterAdvertisers],
  );

  const onCampaignsChange = useCallback(
    (updatedCampaigns: IControlOption[] = []) => {
      if (areArraysOfObjectsEqual(updatedCampaigns, filterCampaigns)) {
        return;
      }
      _setFilters({
        campaigns: updatedCampaigns,
        ...campaignsReset,
      });
    },
    [_setFilters, filterCampaigns],
  );

  const handlerClear = useCallback(() => {
    dispatch(resetFilters());
  }, [dispatch]);

  useEffect(() => {
    if (whitelabels?.length === 1 && !hasInitialized.current) {
      _setFilters({
        whitelabels: whitelabels.map(mapWhitelabelToControlOption),
        ...whitelabelsReset,
      });
      hasInitialized.current = true;
    }
  }, [whitelabels, _setFilters]);

  return (
    <Paper className='h-max p-[24px] pt-[20px] print:p-[10px] flex-wrap'>
      <div className='flex justify-between mb-[16px]'>
        <h2 className='text-[#020202] font-bold text-2xl'>Filtering</h2>
        <ClearAllButton
          onClick={handlerClear}
          sx={{
            fontSize: 14,
            displayPrint: 'none',
          }}
        />
      </div>
      <FilterSelects
        dateStart={dateRangeStart}
        dateEnd={dateRangeEnd}
        dateRangeFormat={dateRangeFormat}
        setDateRange={setDateRange}
        whitelabels={options.whitelabel as IWhitelabelControlOption[]}
        filterWhitelabels={filterWhitelabels}
        whitelabelsDisabled={loading}
        hideWhitelabels={hideWhitelabels}
        onWhitelabelsChange={onWhitelabelsChange}
        clients={options.client}
        filterClients={filterClients}
        clientsDisabled={loading}
        hideClients={hideClients}
        onClientsChange={onClientsChange}
        advertisers={options.advertiser as IControlOption[]}
        filterAdvertisers={filterAdvertisers}
        onAdvertisersChange={onAdvertiserChange}
        campaigns={campaigns}
        filterCampaigns={filterCampaigns}
        onCampaignsChange={onCampaignsChange}
        filterEvents={filterEvents}
      />
      <div className='flex justify-between items-center'>
        <FilterPopover />
        <ApplyLoadingButton loading={loadingFilterSource} onClick={onApplyFilters} />
      </div>
    </Paper>
  );
};

export default Filters;
